import styled, { css } from "styled-components";
import { useState } from "react";
import { Columns } from "../../styles/functions";
import { TypeBodyL, TypeBodyM, TypeCaptionL } from "../../styles/mixins";
import SanityContent from "../sanity/content/SanityContent";
import ProductAccordion from "./ProductAccordion";
import ProductBreadcrumbs from "./ProductBreadcrumbs";
import ProductSpecs from "./ProductSpecs";
import ProductOverview from "./ProductOverview";
import ProductSlideshow from "./ProductSlideshow";
import ProductOverviewCaptions from "./ProductOverviewCaptions";
import { useProduct } from "../../contexts/ProductProvider";
import ProductPrice from "./ProductPrice";
import ButtonInline from "../button/ButtonInline";
import { scrollToAnchor } from "../../hooks/useScrollToAnchor";
import ProductOverlayGallery from "./ProductOverlayGallery";

const ProductLandingMobile: React.FC = ({ ...props }) => {
  const { product, shopifyProduct, selectedVariant } = useProduct();
  const [activeDetail, setActiveDetail] = useState(-1);
  const [slideIndex, setSlideIndex] = useState(0);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  const isAvailable = !!shopifyProduct.variants.edges.find(
    (v) => v.node.availableForSale,
  );
  const label = !isAvailable ? "Sold Out" : product.collectionLabel;

  return (
    <Container {...props}>
      <Breadcrumbs product={product} />

      <PriceWrap>
        <Specs
          withSku={true}
          withStyle={true}
          withHandle={true}
          withSpecs={true}
          withColour={false}
          withColours={true}
        />
        <Price variant={selectedVariant} />
      </PriceWrap>

      <Introduction layout="DEFAULT" blocks={product.introduction} />

      <SlideshowWrapper>
        {label && <Label>{label}</Label>}

        <Slideshow slides={product.gallery} setSlide={setSlideIndex} />

        <SlideshowCaption>
          <p>
            Overview&nbsp;
            {`${slideIndex + 1}`.padStart(2, "0")}/
            {`${product.gallery?.length}`.padStart(2, "0")}
            <br />
            <Button onClick={() => scrollToAnchor("product-overview")}>
              Product Detail
            </Button>
            <br />
            {product.packing && (
              <Button onClick={() => scrollToAnchor("packing")}>
                What this Style Holds
              </Button>
            )}
          </p>
          <Content layout="DEFAULT" blocks={product.content} />
        </SlideshowCaption>
      </SlideshowWrapper>

      <ProductAccordion rows={product.accordion} />

      <Overview
        layout={product.overviewLayout}
        shopifyProduct={shopifyProduct}
        activeDetail={activeDetail}
        setActiveDetail={setActiveDetail}
        setIsOverlayOpen={setIsOverlayOpen}
      />

      <OverviewCaptions
        shopifyProduct={shopifyProduct}
        setActiveDetail={setActiveDetail}
        activeDetail={activeDetail}
        setIsOverlayOpen={setIsOverlayOpen}
      />

      {isOverlayOpen && (
        <ProductOverlayGallery
          initialIndex={activeDetail}
          shopifyProduct={shopifyProduct}
          setIsOverlayOpen={setIsOverlayOpen}
        />
      )}
    </Container>
  );
};

const Breadcrumbs = styled(ProductBreadcrumbs)`
  margin-top: calc(var(--header-height) + 8px);
  margin-bottom: 32px;
`;

const PriceWrap = styled.div`
  position: relative;
  display: flex;
`;

const Specs = styled(ProductSpecs)`
  margin-bottom: 24px;
`;

const Price = styled(ProductPrice)`
  position: absolute;
  top: 0;
  right: 0;
`;

const Introduction = styled(SanityContent)`
  ${TypeBodyL};
  margin-bottom: 32px;
`;

const SlideshowWrapper = styled.div`
  position: relative;
`;

const Label = styled.div`
  ${TypeCaptionL}
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 20;
`;

const Slideshow = styled(ProductSlideshow)`
  margin-bottom: 24px;
`;

const SlideshowCaption = styled.div`
  ${TypeCaptionL};
  margin-bottom: 24px;
`;

const Overview = styled(ProductOverview)`
  margin-top: 40px;
  margin-bottom: 24px;
`;

const Button = styled(ButtonInline)`
  --ButtonInline-color: var(--color-charcoal400);
  --ButtonInline-hover: var(--color-foreground);
`;

const OverviewCaptions = styled(ProductOverviewCaptions)`
  margin-bottom: 80px;
`;

const Content = styled(SanityContent)`
  ${TypeBodyM};
  margin-top: 16px;
  margin-right: ${Columns(2, 0)};
`;

const Container = styled.div``;

export default ProductLandingMobile;
